import React from "react";

export type Column<T> = keyof T | { key: keyof T; label: string, type: string, width?: string };

type GridProps<T extends Record<string, any>> = {
  columns: Column<T>[];
  data: T[];
  setData: (newData: T[]) => void;
  maxHeight?: string | number;
  setIsDirty: React.Dispatch<React.SetStateAction<boolean>>;
};

export const Grid = <T extends Record<string, any>>({
  columns,
  data,
  setData,
  maxHeight = 400,
  setIsDirty,
}: GridProps<T>) => {
  const containerRef = React.useRef<HTMLDivElement>(null);

  const isColumnObject = (col: Column<T>): col is { key: keyof T; label: string, type: string, width?: string } => {
    return typeof col === "object" && col !== null && "label" in col;
  };

  const getLabel = (col: Column<T>): string => {
    if (isColumnObject(col)) {
      return col.label;
    } else {
      return String(col);
    }
  };

  const getKey = (col: Column<T>): keyof T => {
    if (isColumnObject(col)) {
      return col.key;
    } else {
      return col;
    }
  };

  const getType = (col: Column<T>): keyof T => {
    if (isColumnObject(col)) {
      return col.type;
    } else {
      return col;
    }
  };

  const getWidth = (col: Column<T>): keyof T => {
    if (isColumnObject(col)) {
      return col.width ?? 'auto';
    } else {
      return col;
    }
  };

  const createBlankRow = (): T => {
    const row: any = {};
    columns.forEach(col => {    
      row[getKey(col)] = "" as unknown as T[keyof T];
    });
    return row as T;
  };

  const normalizedData: T[] = React.useMemo(() => {
    if (!data.length || Object.values(data[data.length - 1]).some(v => v !== "")) {
      return [...data, createBlankRow()];
    } else {
      return data;
    }
  }, [data, columns]);

  const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    event.preventDefault();
    const text = event.clipboardData.getData("text").trimEnd();
    const lines = text.split(/\r?\n/).filter(line => line.trim() !== "");

    const newData = [...normalizedData];
    const newItem = newData.pop();

    if (!newItem) {
      return;
    }

    const firstKey = getKey(columns[0]);
    const active = document.activeElement as HTMLInputElement | null;
    const rowIndex = active ? Number(active.getAttribute("data-row")) : -1;
    const colKey = active ? (active.getAttribute("data-col") as keyof T) : firstKey;

    if (lines.length === 1) {
      if (rowIndex >= 0 && rowIndex < newData.length && active) {
        const currentValue = newData[rowIndex][colKey] as unknown as string;
        const start = active.selectionStart || 0;
        const end = active.selectionEnd || 0;
        const newValue = currentValue.substring(0, start) + lines[0] + currentValue.substring(end);
        newData[rowIndex] = {
          ...newData[rowIndex],
          [colKey]: newValue as unknown as T[keyof T]
        };
        setTimeout(() => {
          active.setSelectionRange(start + lines[0].length, start + lines[0].length);
        }, 0);
      } else {
        newItem[firstKey] = (newItem[firstKey]
          ? `${newItem[firstKey]}${lines[0]}`
          : lines[0]) as unknown as T[keyof T];
      }
    } else {
      for (const line of lines) {
        const trimmed = line.trim();

        if (!trimmed) {
          continue;
        }

        const exists = newData.some(x => x[firstKey] === trimmed);

        if (!exists) {
          const item: any = {};

          columns.forEach(col => {
            item[getKey(col)] = "" as unknown as T[keyof T];
          });

          item[firstKey] = trimmed as unknown as T[keyof T];
          newData.push(item);
        }
      }
    }

    newData.push(newItem);
    setData(newData);
    setIsDirty(true);
  };

  const handleCellBlur = (rowIndex: number) => {
    if (rowIndex === normalizedData.length - 1) {
      return;
    }

    const row = normalizedData[rowIndex];

    if (Object.values(row).every(v => v === "")) {
      const newData = normalizedData.filter((_, i) => i !== rowIndex);
      setData(newData);
      const allEmpty = newData.every(r => Object.values(r).every(v => v === ""));

      if (allEmpty) {
        setIsDirty(false);
      }
    }
  };

  const handleCellChange = (rowIndex: number, column: keyof T, value: any) => {
    const newData = [...normalizedData];
    newData[rowIndex] = {
      ...newData[rowIndex],
      [column]: value as unknown as T[keyof T]
    };
    const lastRow = newData[newData.length - 1];
    if (Object.values(lastRow).some(v => v !== "")) {
      newData.push(createBlankRow());
    }
    setData(newData);
    setIsDirty(true);
  };

  const handleDeleteRow = (rowIndex: number) => {
    const newData = normalizedData.filter((_, i) => i !== rowIndex);
    setData(newData);
    setIsDirty(true);
  };

  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    const scrollToBottom = () => {
      container.scrollTop = container.scrollHeight;
    };
    if (container.offsetParent !== null) {
      scrollToBottom();
    } else {
      const interval = setInterval(() => {
        if (container.offsetParent !== null) {
          scrollToBottom();
          clearInterval(interval);
        }
      }, 50);
    }
  }, [normalizedData.length]);

  return (
    <div
      ref={containerRef}
      style={{
        maxHeight,
        overflowY: "auto",
        border: "1px solid #ccc"
      }}
      onPaste={handlePaste}
    >
      <table style={{ borderCollapse: "collapse", width: "100%" }}>
        <thead
          style={{
            position: "sticky",
            top: 0,
            background: "#f9f9f9",
            zIndex: 2
          }}
        >
          <tr>
            {columns.map(col => {
              return (
                <th
                  key={String(getKey(col))}
                  style={{
                    border: "1px solid #ccc",
                    padding: "8px",
                    background: "#f3f3f3",
                    textAlign: "left"
                  }}
                >
                  {getLabel(col)}
                </th>
              );
            })}
            <th
              style={{
                border: "1px solid #ccc",
                padding: "8px",
                background: "#f3f3f3",
                textAlign: "center",
                width: "50px"
              }}
            >
            </th>
          </tr>
        </thead>
        <tbody>
          {normalizedData.map((row, rowIndex) => {
            return (
              <tr key={rowIndex}>
                {columns.map(col => {
                  const key = getKey(col);
                  const value = row[key];
                  const type = getType(col);
                  const width = getWidth(col) as string;

                  return (
                    <td
                      key={String(key)}
                      style={{
                        border: "1px solid #ccc",
                        padding: "8px",
                        textAlign: "center",
                        verticalAlign: "middle",
                        width: width
                      }}
                    >
                      {type === 'checkbox' ? (
                        <input
                          type="checkbox"
                          checked={value as boolean}
                          onChange={(e) => handleCellChange(rowIndex, key, e.target.checked)}
                           style={{
                            width: "18px",
                            height: "18px",
                            cursor: "pointer",
                            transform: "scale(1.1)",
                            accentColor: "#0078d4"
                          } as React.CSSProperties}
                        />
                      ) : (
                        <input
                          data-row={rowIndex}
                          data-col={String(key)}
                          value={value as unknown as string}
                          onChange={(e) => handleCellChange(rowIndex, key, e.target.value)}
                          onBlur={() => handleCellBlur(rowIndex)}
                          style={{ width: "100%" }}
                        />
                      )}
                    </td>
                  );
                })}
                <td
                  style={{
                    border: "1px solid #ccc",
                    padding: "8px",
                    textAlign: "center",
                    verticalAlign: "middle",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => handleDeleteRow(rowIndex)}
                    style={{
                      backgroundColor: "#ff4d4f",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      padding: "4px 8px"
                    }}
                  >
                    ✕
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
